import {
  AgentFlowOptIn,
  AgentFlowProgress,
  dispatch,
  getAgentDataFromFirebase,
  getUserId,
  store,
  updateAgentDataFromFirebase,
} from '@zonofi/seller-rtk-store';
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  writeBatch,
  addDoc,
  query,
  limit,
  where,
} from 'firebase/firestore';

const IS_PROD_ENV = process.env.NX_APP_ENV === 'PROD';

// Agent JTBD Flow
/** Initializes the agent flow for a user in firebase */
export const initializeAgentFlow = async (userId: string) => {
  try {
    const agentFlowItemsRef = collection(
      globalThis.sellerFirebasedb,
      IS_PROD_ENV ? 'users_prod' : 'users_qa',
      userId,
      'agent_flow'
    );

    const agentFlowItems = [
      { title: 'upload_knowledge', order: 1 },
      { title: 'test_agent', order: 2 },
      { title: 'waba_success', order: 3 },
    ];

    const agentFlowItemsSnapshot = await getDocs(agentFlowItemsRef);

    // Check if agent flow items exist
    const hasAgentFlowItems = !agentFlowItemsSnapshot.empty;

    // Check if optedIn field exists
    const optedInQuery = query(
      agentFlowItemsRef,
      where('optedIn', '!=', null),
      limit(1)
    );
    const optedInSnapshot = await getDocs(optedInQuery);
    const hasOptedInField = !optedInSnapshot.empty;

    let agentFlowUpdatedItems = [];

    // Initialize agent flow items if they don't exist
    if (!hasAgentFlowItems) {
      for (const agentFlowItem of agentFlowItems) {
        const agentFlowItemDoc = await addDoc(agentFlowItemsRef, {
          title: agentFlowItem.title,
          status: 'pending',
          order: agentFlowItem.order,
          createdAt: new Date().toISOString(),
        });
        agentFlowUpdatedItems.push(agentFlowItemDoc);
        console.log(
          `Initialized agent flow item ${agentFlowItem.title} for user ${userId} : `,
          ' docId : ',
          agentFlowItemDoc?.id
        );
      }
    } else {
      console.log(`Agent flow already initialized for user ${userId}`);
    }

    // Add optedIn field if it doesn't exist
    if (!hasOptedInField) {
      const optedInDoc = await addDoc(agentFlowItemsRef, {
        optedIn: false,
        createdAt: new Date().toISOString(),
      });
      agentFlowUpdatedItems.push(optedInDoc);
      console.log(
        `Added optedIn field for user ${userId} : `,
        ' docId : ',
        optedInDoc?.id
      );
    } else {
      console.log(`OptedIn field already exists for user ${userId}`);
    }

    return agentFlowUpdatedItems;
  } catch (error) {
    throw new Error(`Error initializing agent flow for user: ${error}`);
  }
};

/** fetches latest agent flow data from firebase : "users_<env>/<userId>/agent_flow" and returns the data. */
export const getAgentFlowProgress = async () => {
  console.log('Getting firebase agent_flow data');
  const userId = getUserId(store.getState());

  console.log('userId from firebasecontroller :', userId);

  if (userId) {
    const agentFlowRef = collection(
      globalThis.sellerFirebasedb,
      IS_PROD_ENV ? 'users_prod' : 'users_qa',
      userId,
      'agent_flow'
    );
    console.log('agentFlowRef from firebasecontroller :', agentFlowRef);
    const snapshot = await getDocs(agentFlowRef);

    console.log('snapshot: ', snapshot);
    //@ts-ignore
    const agentFlowDataFromFirebase: AgentFlowProgress[] = snapshot.docs.map(
      (doc) => {
        const data = doc.data();

        return { id: doc.id, ...data };
      }
    );
    dispatch(updateAgentDataFromFirebase(agentFlowDataFromFirebase));
    return agentFlowDataFromFirebase;
  }

  return [];
};

/** Updates the agent flow items's status from pending to completed in users_<env>/<userId>/checklist in firebase.userId>/checklist". If the item's status is already completed, it simply returns.
 * @param {string} itemId - The id of the checklist item to update
 */
export const updateAgentFlowProgress = async (order: number) => {
  const userId = getUserId(store.getState());
  const agentFlowDataFromFirebase = await getAgentFlowProgress();

  const targetItem = agentFlowDataFromFirebase?.find(
    (i) => 'order' in i && i.order === order
  );

  if (
    targetItem &&
    'order' in targetItem &&
    targetItem.status !== 'completed'
  ) {
    console.log(
      'item id from updateAgentFlowProgress: ',
      targetItem.id,
      'item status from updateAgentFlowProgress: ',
      targetItem.status
    );

    const itemRef = doc(
      globalThis.sellerFirebasedb,
      IS_PROD_ENV ? 'users_prod' : 'users_qa',
      userId,
      'agent_flow',
      targetItem.id
    );

    await updateDoc(itemRef, {
      status: 'completed',
      updatedAt: new Date().toISOString(),
    });

    getAgentFlowProgress();
  }
};

/** updates agent-flow-status by adding opted-in as true in the agent checklist from firebase : "users_<env>/<userId>/agent_flow" and returns true. */
export const updateOptedInstatus = async () => {
  const userId = getUserId(store.getState());
  const optedInDoc = getAgentDataFromFirebase(store.getState());
  console.log('updated field for userId for opted-in state : ', userId);
  const optedInDocId = optedInDoc?.find((i) => 'optedIn' in i)?.id;
  console.log('optedIn field id for opted-in state : ', optedInDocId);

  if (
    optedInDoc?.find((i): i is AgentFlowOptIn => 'optedIn' in i)?.optedIn ===
    true
  ) {
    console.log('optedIn status already true for user : ', userId);
    return false;
  }

  const itemRef = doc(
    globalThis.sellerFirebasedb,
    IS_PROD_ENV ? 'users_prod' : 'users_qa',
    userId,
    'agent_flow',
    optedInDocId!
  );
  try {
    await updateDoc(itemRef, {
      optedIn: true,
    });
    console.log('Opted-in status updated for user : ', userId);
    getAgentFlowProgress();
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
};

/** Resets the agent flow items's status from completed to pending in users_<env>/<userId>/agent_flow in firebase.userId>/agent_flow". */
export const resetAgentFlowProgress = async () => {
  const userId = getUserId(store.getState());
  const batch = writeBatch(globalThis.sellerFirebasedb);

  const now = new Date().toISOString();

  const agentFlowDataFromFirebase = await getAgentFlowProgress();
  const itemIds = agentFlowDataFromFirebase?.map((i) => i?.id);

  itemIds.forEach((itemId) => {
    const itemRef = doc(
      globalThis.sellerFirebasedb,
      IS_PROD_ENV ? 'users_prod' : 'users_qa',
      userId,
      'agent_flow',
      itemId
    );
    batch.update(itemRef, {
      status: 'pending',
      updatedAt: now,
    });
  });

  await batch.commit();
  getAgentFlowProgress();
};
